import React, { useState } from 'react';
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Edit3,
  FileText,
  HelpCircle,
  Loader2,
  RefreshCw,
  Shield,
  Sparkles,
  XCircle,
} from 'lucide-react';
import Card from '../../components/Card';
import { ReviewStatusBadge } from '../../components/RequestBadges';
import { LongText, Subhead } from './RequestDetailPrimitives';
import {
  renderContractDetails,
  renderSecurityAssessment,
} from './RequestDetailParts';

/*
 * Designer layout (2026-06-11 mocks):
 *
 *   1. AI first-pass briefing — a purple advisory banner up top: recommended
 *      risk + confidence, summary, severity rollup, "why" vs "confirm before
 *      sign-off". Explicitly labeled "Advisory · approves nothing".
 *   2. Sign-off required — one decisive row per lane (status + actions),
 *      with a one-line AI rollup underneath: severity counts + the single
 *      top blocker. "Review all N" expands an inline severity-sorted list
 *      (high items in full; medium/low/questions folded; long lists scroll).
 *   3. Request details — scope + the two kv sections side by side.
 */

const SEVERITY_ORDER = { high: 0, medium: 1, low: 2 };
const SEVERITY_CHIP = { high: 'red', medium: 'yellow', low: 'gray' };

function bucketFlags(flags = []) {
  const buckets = { high: [], medium: [], low: [] };
  for (const flag of flags) {
    const sev = SEVERITY_ORDER[flag.severity] !== undefined ? flag.severity : 'medium';
    buckets[sev].push(flag);
  }
  return buckets;
}

function SeverityChips({ buckets, questions = [], size = 'sm' }) {
  const fontSize = size === 'sm' ? 10.5 : 11;
  const chip = (cls, label) => (
    <span key={label} className={`chip ${cls}`} style={{ fontSize, fontWeight: 600 }}>{label}</span>
  );
  const chips = [];
  if (buckets.high.length) chips.push(chip('red', `${buckets.high.length} HIGH`));
  if (buckets.medium.length) chips.push(chip('yellow', `${buckets.medium.length} MEDIUM`));
  if (buckets.low.length) chips.push(chip('gray', `${buckets.low.length} LOW`));
  if (questions.length) chips.push(chip('gray', `${questions.length} QUESTION${questions.length === 1 ? '' : 'S'}`));
  return chips.length ? <>{chips}</> : null;
}

// One fully-rendered flag (used for HIGH items and inside expanded groups).
function FlagItem({ flag }) {
  return (
    <div className="row" style={{ gap: 10, alignItems: 'flex-start', padding: '10px 0' }}>
      <span
        className={`chip ${SEVERITY_CHIP[flag.severity] || 'yellow'}`}
        style={{ fontSize: 10, fontWeight: 700, flexShrink: 0, marginTop: 1 }}
      >
        {(flag.severity || 'medium').toUpperCase()}
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{flag.title || 'Flag'}</div>
        {flag.detail && (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55, marginTop: 2 }}>
            {flag.detail}
          </div>
        )}
        {flag.policyReference && (
          <div className="mono muted" style={{ fontSize: 11, marginTop: 4, letterSpacing: 0.5 }}>
            {flag.policyReference}
          </div>
        )}
      </div>
    </div>
  );
}

// A folded severity group ("Medium · 4 — Show ˅") that expands in place.
function FoldedGroup({ dotColor, label, count, children }) {
  const [open, setOpen] = useState(false);
  if (!count) return null;
  return (
    <div style={{ borderTop: '0.5px solid var(--border-primary)' }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="row"
        style={{
          width: '100%', background: 'none', border: 'none', cursor: 'pointer',
          padding: '10px 0', gap: 8, alignItems: 'center', justifyContent: 'space-between',
          fontSize: 12.5, color: 'var(--text-primary)',
        }}
      >
        <span className="row" style={{ gap: 8, alignItems: 'center' }}>
          <span style={{ width: 8, height: 8, borderRadius: 2, background: dotColor, flexShrink: 0 }} />
          <span style={{ fontWeight: 600 }}>{label}</span>
          <span className="muted">· {count}</span>
        </span>
        <span className="muted row" style={{ gap: 4, alignItems: 'center', fontSize: 12 }}>
          {open ? 'Hide' : 'Show'}
          {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        </span>
      </button>
      {open && <div style={{ paddingBottom: 6 }}>{children}</div>}
    </div>
  );
}

// The one-line AI rollup under a sign-off row, expanding to the full
// severity-sorted list. Never taller than one line until asked.
function LaneAiRollup({ summary, flags, questions }) {
  const [expanded, setExpanded] = useState(false);
  const buckets = bucketFlags(flags);
  const total = (flags?.length || 0) + (questions?.length || 0);
  if (total === 0 && !summary) return null;

  const topBlocker = buckets.high[0] || null;
  const headline = topBlocker
    ? (
      <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <strong style={{ color: 'var(--text-critical)' }}>Blocker:</strong>{' '}
        <span style={{ color: 'var(--text-secondary)' }}>{topBlocker.title}{topBlocker.detail ? ` — ${topBlocker.detail}` : ''}</span>
      </span>
    )
    : summary
    ? (
      <span style={{ color: 'var(--text-secondary)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {summary}
      </span>
    )
    : null;

  if (!expanded) {
    return (
      <div className="row" style={{ gap: 8, alignItems: 'center', fontSize: 12.5, minWidth: 0, flexWrap: 'wrap' }}>
        <span className="row" style={{ gap: 4, alignItems: 'center', color: 'var(--text-purple)', flexShrink: 0, fontSize: 11.5, fontWeight: 600 }}>
          <Sparkles className="w-3 h-3" />
          AI
        </span>
        <SeverityChips buckets={buckets} questions={questions || []} />
        {headline}
        {total > 0 && (
          <button
            type="button"
            className="btn ghost xs"
            style={{ marginLeft: 'auto', flexShrink: 0 }}
            onClick={() => setExpanded(true)}
          >
            Review all {total}
            <ChevronRight className="w-3 h-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="row" style={{ gap: 8, alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="row" style={{ gap: 5, alignItems: 'center', color: 'var(--text-purple)', fontSize: 11, fontWeight: 600, letterSpacing: 0.5 }}>
          <Sparkles className="w-3 h-3" />
          AI FLAGS · ADVISORY
        </span>
        <button type="button" className="btn ghost xs" onClick={() => setExpanded(false)}>
          Collapse
          <ChevronUp className="w-3 h-3" />
        </button>
      </div>
      <div style={{ maxHeight: 480, overflowY: 'auto' }}>
        {/* High severity always rendered in full — never buried. */}
        {buckets.high.length > 0 && (
          <div>
            <div className="row" style={{ gap: 8, alignItems: 'center', padding: '6px 0', borderBottom: '0.5px solid var(--border-primary)' }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--color-red-500)' }} />
              <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>HIGH</span>
              <span className="muted" style={{ fontSize: 12 }}>{buckets.high.length}</span>
            </div>
            {buckets.high.map((flag, i) => <FlagItem key={i} flag={flag} />)}
          </div>
        )}
        <FoldedGroup dotColor="var(--color-yellow-500)" label="Medium" count={buckets.medium.length}>
          {buckets.medium.map((flag, i) => <FlagItem key={i} flag={flag} />)}
        </FoldedGroup>
        <FoldedGroup dotColor="var(--color-gray-400)" label="Low" count={buckets.low.length}>
          {buckets.low.map((flag, i) => <FlagItem key={i} flag={flag} />)}
        </FoldedGroup>
        {Array.isArray(questions) && questions.length > 0 && (
          <FoldedGroup dotColor="var(--color-gray-400)" label="Open questions" count={questions.length}>
            {questions.map((q, i) => (
              <div key={i} className="row" style={{ gap: 8, alignItems: 'flex-start', padding: '8px 0' }}>
                <HelpCircle className="w-3.5 h-3.5" style={{ color: 'var(--icon-tertiary)', flexShrink: 0, marginTop: 2 }} />
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{q}</span>
              </div>
            ))}
          </FoldedGroup>
        )}
      </div>
    </div>
  );
}

// One card per lane: unmistakable containment — an expanded AI list can
// only ever belong to the lane whose card it sits in, and both lanes'
// statuses stay visible in their own headers. (Tabs were rejected: they
// hide one lane behind the other.)
function SignoffCard({
  lane, title, icon: Icon, status, reviewedBy, reviewedAt, notes,
  canAct, onAction, formatDateTime, aiSummary, aiFlags, aiQuestions,
}) {
  return (
    <Card
      title={`${title} sign-off`}
      icon={Icon}
      action={
        <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <ReviewStatusBadge status={status || 'pending'} variant="signoff" />
          {canAct && (
            <>
              <button className="btn primary sm" onClick={() => onAction(lane, 'approve')}>
                <CheckCircle className="w-3.5 h-3.5" />
                Approve
              </button>
              <button className="btn outline sm" onClick={() => onAction(lane, 'request_changes')}>
                <Edit3 className="w-3.5 h-3.5" />
                Request info
              </button>
              <button className="btn outline sm" onClick={() => onAction(lane, 'reject')}>
                <XCircle className="w-3.5 h-3.5" />
                Reject
              </button>
            </>
          )}
        </div>
      }
    >
      <div className="stack-md">
        {(aiSummary || aiFlags?.length > 0 || aiQuestions?.length > 0) ? (
          <LaneAiRollup summary={aiSummary} flags={aiFlags} questions={aiQuestions} />
        ) : (
          !reviewedBy && !notes && <div className="muted" style={{ fontSize: 12.5 }}>Awaiting review.</div>
        )}
        {(reviewedBy || notes) && (
          <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            {reviewedBy && (
              <span className="muted">
                Last reviewed by {reviewedBy}{reviewedAt ? ` · ${formatDateTime(reviewedAt)}` : ''}
              </span>
            )}
            {notes && <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{notes}</div>}
          </div>
        )}
      </div>
    </Card>
  );
}

// Confidence meter: three segments, filled per low/medium/high.
function ConfidenceMeter({ confidence }) {
  const filled = { low: 1, medium: 2, high: 3 }[confidence] || 2;
  return (
    <div>
      <div className="row" style={{ gap: 4, marginBottom: 4 }}>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            style={{
              height: 4, flex: 1, borderRadius: 999,
              background: i < filled ? 'var(--color-purple-500)' : 'var(--color-gray-200)',
            }}
          />
        ))}
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>
        {confidence || 'medium'}
      </span>
    </div>
  );
}

function BulletList({ items, dotColor }) {
  if (!items?.length) return null;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      {items.map((item, i) => (
        <div key={i} className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
          <span style={{ width: 6, height: 6, borderRadius: 999, background: dotColor, flexShrink: 0, marginTop: 6 }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{item}</span>
        </div>
      ))}
    </div>
  );
}

const RISK_DOT = {
  low: 'var(--color-green-500)',
  medium: 'var(--color-yellow-500)',
  high: 'var(--color-red-500)',
};

// The advisory briefing banner. States: running (poll note), failed, stale,
// complete. Hidden entirely pre-submission.
function AiBriefingCard({
  request, firstPass, legalFlags, securityFlags,
  hasElevatedAccess, handleRerunFirstPass, actionLoading, formatDateTime,
}) {
  // The reasons/follow-ups detail folds away by default to keep the
  // briefing to a few lines; the chips above carry the counts.
  const [detailOpen, setDetailOpen] = useState(false);
  const status = request.firstPassStatus;
  if (!firstPass && (!status || status === 'not_started')) return null;

  const allFlags = [...(legalFlags || []), ...(securityFlags || [])];
  const buckets = bucketFlags(allFlags);
  const followUps = firstPass?.requiredFollowUps || [];
  const risk = firstPass?.recommendedRiskRating || 'medium';
  const canRerun = hasElevatedAccess
    && ['submitted', 'under_review', 'changes_requested', 'approved'].includes(request.status);

  return (
    <div
      className="card"
      style={{
        border: '0.5px solid var(--surface-purple)',
        background: 'linear-gradient(180deg, var(--surface-purple) 0%, var(--surface-primary) 120%)',
      }}
    >
      <div className="card-body" style={{ padding: '18px 22px' }}>
        <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <Sparkles className="w-4 h-4" style={{ color: 'var(--text-purple)' }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-purple)' }}>AI first-pass briefing</span>
          <span className="muted" style={{ fontSize: 12.5 }}>for reviewers</span>
          <div className="row" style={{ gap: 8, marginLeft: 'auto', alignItems: 'center' }}>
            <span className="chip purple" style={{ fontSize: 11 }}>Advisory · approves nothing</span>
            {canRerun && (
              <button onClick={handleRerunFirstPass} disabled={actionLoading} className="btn ghost xs" title="Rerun the first-pass">
                {actionLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        </div>

        {(status === 'queued' || status === 'running') && (
          <div className="row muted" style={{ gap: 8, alignItems: 'center', fontSize: 13 }}>
            <Loader2 className="w-4 h-4 animate-spin" />
            Reading the intake and uploaded documents…
          </div>
        )}

        {status === 'failed' && (
          <div className="callout red">
            <div className="callout-body" style={{ fontSize: 12 }}>
              <strong>First-pass failed.</strong> {request.firstPassError}
            </div>
          </div>
        )}

        {firstPass && !['queued', 'running', 'failed'].includes(status) && (
          <>
            {status === 'stale' && (
              <div className="callout yellow" style={{ marginBottom: 12 }}>
                <div className="callout-body" style={{ fontSize: 12 }}>
                  The request changed after this briefing was generated — rerun for a fresh read.
                </div>
              </div>
            )}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(150px, 200px) 1fr',
                gap: 22,
                alignItems: 'start',
              }}
            >
              <div style={{ borderRight: '0.5px solid var(--border-primary)', paddingRight: 22 }}>
                <div className="muted" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6 }}>
                  Recommended risk
                </div>
                <div className="row" style={{ gap: 8, alignItems: 'center', marginBottom: 14 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 999, background: RISK_DOT[risk] || RISK_DOT.medium }} />
                  <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-primary)', textTransform: 'capitalize' }}>{risk}</span>
                </div>
                <div className="muted" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 6 }}>
                  Confidence
                </div>
                <ConfidenceMeter confidence={firstPass.confidence} />
              </div>
              <div>
                {firstPass.overallSummary && <LongText>{firstPass.overallSummary}</LongText>}
                <div className="row" style={{ gap: 6, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                  <SeverityChips buckets={buckets} size="md" />
                  {followUps.length > 0 && (
                    <span className="chip gray" style={{ fontSize: 11, fontWeight: 600 }}>
                      {followUps.length} FOLLOW-UP{followUps.length === 1 ? '' : 'S'}
                    </span>
                  )}
                  {request.firstPassGeneratedAt && (
                    <span className="muted mono" style={{ fontSize: 11, marginLeft: 'auto' }}>
                      {formatDateTime(request.firstPassGeneratedAt)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {(firstPass.keyReasons?.length > 0 || followUps.length > 0) && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '0.5px solid var(--border-primary)' }}>
                <button
                  type="button"
                  className="row"
                  onClick={() => setDetailOpen((v) => !v)}
                  style={{
                    width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                    padding: 0, gap: 8, alignItems: 'center', justifyContent: 'space-between',
                  }}
                >
                  <span className="muted" style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    Why {risk}
                    {firstPass.keyReasons?.length > 0 && ` · ${firstPass.keyReasons.length} reason${firstPass.keyReasons.length === 1 ? '' : 's'}`}
                    {followUps.length > 0 && ` · ${followUps.length} to confirm`}
                  </span>
                  <span className="muted row" style={{ gap: 4, alignItems: 'center', fontSize: 12, flexShrink: 0 }}>
                    {detailOpen ? 'Hide' : 'Show'}
                    {detailOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  </span>
                </button>
                {detailOpen && (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                      gap: 22,
                      marginTop: 12,
                    }}
                  >
                    {firstPass.keyReasons?.length > 0 && (
                      <div>
                        <div className="muted" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8 }}>
                          Why {risk}
                        </div>
                        <BulletList items={firstPass.keyReasons} dotColor="var(--color-blue-500)" />
                      </div>
                    )}
                    {followUps.length > 0 && (
                      <div>
                        <div className="muted" style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', marginBottom: 8 }}>
                          Confirm before sign-off
                        </div>
                        <BulletList items={followUps} dotColor="var(--color-yellow-500)" />
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {Array.isArray(firstPass.documentsConsidered) && firstPass.documentsConsidered.length > 0 && (
              <div className="muted" style={{ fontSize: 11, marginTop: 12 }}>
                Documents read: {firstPass.documentsConsidered.join(', ')}
              </div>
            )}
            {firstPass.llmNotes && (
              <div className="callout yellow" style={{ marginTop: 12 }}>
                <div className="callout-body" style={{ fontSize: 12 }}>{firstPass.llmNotes}</div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function RequestDetailReviewPanel({
  actionLoading,
  canReviewLegal,
  canReviewSecurity,
  firstPass,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatYesNo,
  handleRerunFirstPass,
  hasElevatedAccess,
  legalFlags,
  openReviewModal,
  request,
  securityFlags,
}) {
  return (
    <>
      <SignoffCard
        lane="legal"
        title="Legal"
        icon={FileText}
        status={request.legalReviewStatus}
        reviewedBy={request.legalReviewedByName}
        reviewedAt={request.legalReviewedAt}
        notes={request.legalReviewNotes}
        canAct={canReviewLegal}
        onAction={openReviewModal}
        formatDateTime={formatDateTime}
        aiSummary={firstPass?.legal?.summary}
        aiFlags={legalFlags}
        aiQuestions={firstPass?.legal?.questions}
      />

      <SignoffCard
        lane="security"
        title="Security"
        icon={Shield}
        status={request.securityReviewStatus}
        reviewedBy={request.securityReviewedByName}
        reviewedAt={request.securityReviewedAt}
        notes={request.securityReviewNotes}
        canAct={canReviewSecurity}
        onAction={openReviewModal}
        formatDateTime={formatDateTime}
        aiSummary={firstPass?.security?.summary}
        aiFlags={securityFlags}
        aiQuestions={firstPass?.security?.questions}
      />

      <AiBriefingCard
        request={request}
        firstPass={firstPass}
        legalFlags={legalFlags}
        securityFlags={securityFlags}
        hasElevatedAccess={hasElevatedAccess}
        handleRerunFirstPass={handleRerunFirstPass}
        actionLoading={actionLoading}
        formatDateTime={formatDateTime}
      />

      <Card title="Request details" icon={FileText}>
        <div className="stack-md">
          {(request.scope || request.serviceDescription) && (
            <div>
              {request.scope && (
                <div style={{ marginBottom: request.serviceDescription ? 10 : 0 }}>
                  <Subhead>Scope</Subhead>
                  <LongText>{request.scope}</LongText>
                </div>
              )}
              {request.serviceDescription && (
                <div>
                  <Subhead>Service description</Subhead>
                  <LongText>{request.serviceDescription}</LongText>
                </div>
              )}
            </div>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 18,
              paddingTop: (request.scope || request.serviceDescription) ? 14 : 0,
              borderTop: (request.scope || request.serviceDescription) ? '0.5px solid var(--border-primary)' : 0,
            }}
          >
            <div>
              <Subhead>Security assessment</Subhead>
              {renderSecurityAssessment(request, formatYesNo)}
            </div>
            <div>
              <Subhead>Contract details</Subhead>
              {renderContractDetails(request, formatDate, formatYesNo, formatCurrency)}
            </div>
          </div>
        </div>
      </Card>
    </>
  );
}
